// Yes/no calendar body — the default chart for a scalar `bool` feature.
//
// The same three-month comb as ActivityCalendarBody (and the same nav), but
// each day shows the day's *answer* rather than a checkin count, Yes when any
// checkin that day says so (`all` is a summary setting, not a chart one):
// honey for Yes, stone for No, the empty cell for a day with no answer. Unrecorded days are never drawn or counted as No
// (docs/bool_features.md, "Unknown is not No").
//
// The header counts over the visible months, from the first day the feature
// was ever answered through today, so the weeks before it existed don't pile
// up as "not recorded".

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import ActivityCalendar, { HexCell } from '../../components/ActivityCalendar';
import { boolLabels } from '../../utils/boolFeature';
import type { ChartBodyProps } from '../types';
import { boolDayAnswers, countBoolDays, isoDay, type BoolValueRow } from './boolAggregation';
import { BOOL_COLORS } from './chartColors';

const MONTHS_TO_SHOW = 3;
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const LegendSwatch: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <span className="inline-flex items-center gap-1">
    <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: color }} />
    {label}
  </span>
);

const BoolCalendarBody: React.FC<ChartBodyProps> = ({ featureId, config, fullscreen }) => {
  const [rows, setRows] = useState<BoolValueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const today = new Date();
  const [endYear, setEndYear] = useState(today.getFullYear());
  const [endMonth, setEndMonth] = useState(today.getMonth());
  const [anchorPicked, setAnchorPicked] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const [trueLabel, falseLabel] = boolLabels(config?.params);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setAnchorPicked(false);
    api.get<BoolValueRow[]>(`/api/features/values/${featureId}`)
      .then((resp) => {
        if (cancelled) return;
        setRows(resp.data);
        setError(null);
      })
      .catch((err) => {
        console.error('Failed to fetch feature values:', err);
        if (!cancelled) setError('Failed to load data');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [featureId]);

  const answers = useMemo(() => boolDayAnswers(rows, 'any'), [rows]);
  const answeredDays = useMemo(() => [...answers.keys()].sort(), [answers]);

  // Land on the month of the latest answer (or today, if later / none).
  useEffect(() => {
    if (loading || anchorPicked) return;
    const latest = answeredDays.length > 0
      ? new Date(answeredDays[answeredDays.length - 1] + 'T00:00:00')
      : null;
    const now = new Date();
    const target = !latest || latest > now ? now : latest;
    setEndYear(target.getFullYear());
    setEndMonth(target.getMonth());
    setAnchorPicked(true);
  }, [loading, answeredDays, anchorPicked]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollLeft = el.scrollWidth;
  }, [endYear, endMonth, rows.length]);

  const monthsToDisplay = useMemo(() => {
    const out: { month: number; year: number }[] = [];
    for (let i = MONTHS_TO_SHOW - 1; i >= 0; i--) {
      let m = endMonth - i;
      let y = endYear;
      while (m < 0) { m += 12; y -= 1; }
      out.push({ month: m, year: y });
    }
    return out;
  }, [endMonth, endYear]);

  const todayIso = isoDay(today);
  const counts = useMemo(() => {
    const first = monthsToDisplay[0];
    const last = monthsToDisplay[monthsToDisplay.length - 1];
    const viewFrom = isoDay(new Date(first.year, first.month, 1));
    const viewTo = isoDay(new Date(last.year, last.month + 1, 0));
    const firstAnswer = answeredDays[0];
    const lastAnswer = answeredDays[answeredDays.length - 1];
    if (!firstAnswer) return countBoolDays(answers, viewFrom, viewTo);
    const upTo = lastAnswer > todayIso ? lastAnswer : todayIso;
    const from = firstAnswer > viewFrom ? firstAnswer : viewFrom;
    const to = upTo < viewTo ? upTo : viewTo;
    return countBoolDays(answers, from, to);
  }, [monthsToDisplay, answers, answeredDays, todayIso]);

  const goPrevious = () => {
    let m = endMonth - MONTHS_TO_SHOW;
    let y = endYear;
    while (m < 0) { m += 12; y -= 1; }
    setEndMonth(m); setEndYear(y);
  };
  const goNext = () => {
    let m = endMonth + MONTHS_TO_SHOW;
    let y = endYear;
    while (m > 11) { m -= 12; y += 1; }
    const now = new Date();
    if (y > now.getFullYear() || (y === now.getFullYear() && m > now.getMonth())) {
      setEndYear(now.getFullYear());
      setEndMonth(now.getMonth());
    } else {
      setEndMonth(m); setEndYear(y);
    }
  };
  const goToday = () => {
    const now = new Date();
    setEndYear(now.getFullYear());
    setEndMonth(now.getMonth());
  };
  const isCurrentPeriod = endYear === today.getFullYear() && endMonth === today.getMonth();

  const renderCell = (date: string) => {
    const formatted = new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    });
    const answer = answers.get(date);
    const color = answer === true ? BOOL_COLORS.yes : answer === false ? BOOL_COLORS.no : BOOL_COLORS.unknown;
    const state = answer === true ? trueLabel : answer === false ? falseLabel : 'not recorded';
    return (
      <HexCell color={color} title={date > todayIso && answer === undefined ? formatted : `${formatted}: ${state}`} />
    );
  };

  if (loading) {
    return (
      <div className={`flex justify-center items-center ${fullscreen ? 'h-full' : 'h-64'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600" />
      </div>
    );
  }
  if (error) {
    return <div className="text-center py-8 text-rose-500"><p>{error}</p></div>;
  }
  if (answers.size === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>Nothing recorded yet.</p>
      </div>
    );
  }

  const header = counts.recorded === 0
    ? 'Nothing recorded'
    : `${trueLabel} on ${counts.yes} of ${counts.recorded} day${counts.recorded === 1 ? '' : 's'}`
      + (counts.notRecorded > 0 ? ` · ${counts.notRecorded} not recorded` : '');

  return (
    <div className={fullscreen ? 'h-full overflow-auto px-2 py-4' : 'px-2 py-2'}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <button
            onClick={goPrevious}
            className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
            title="Previous months"
          >
            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={goNext}
            className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
            title="Next months"
            disabled={isCurrentPeriod}
          >
            <svg className={`w-4 h-4 ${isCurrentPeriod ? 'text-gray-300' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          {!isCurrentPeriod && (
            <button
              onClick={goToday}
              className="text-sm text-amber-800 hover:text-amber-900 underline ml-1"
              title="Go to current month"
            >
              Today
            </button>
          )}
        </div>
        <div className="text-sm text-gray-700 tabular-nums">{header}</div>
      </div>

      <div ref={scrollRef} className="flex gap-4 overflow-x-auto">
        {monthsToDisplay.map(({ month, year }) => (
          <div key={`${year}-${month}`} className="flex-shrink-0">
            <div className="text-[10px] font-medium text-gray-500 mb-1 text-center">
              {MONTH_SHORT[month]} {year !== today.getFullYear() ? `'${String(year).slice(-2)}` : ''}
            </div>
            <ActivityCalendar
              year={year}
              month={month}
              activityData={{}}
              renderCell={renderCell}
            />
          </div>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
        <LegendSwatch color={BOOL_COLORS.yes} label={trueLabel} />
        <LegendSwatch color={BOOL_COLORS.no} label={falseLabel} />
        <LegendSwatch color={BOOL_COLORS.unknown} label="not recorded" />
      </div>
    </div>
  );
};

export default BoolCalendarBody;
